/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box, Stack, Typography, Alert, Divider } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";
import { EditableHotkey } from "../EditableHotkey";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { EventExampleVideoPlayer } from "../EventExampleVideoPlayer";
import { getModifierKey } from "hooks/usePlatform";
import { useAreaHotkeys } from "components/scrapEyeLabelling/hooks/useAreaHotkeys";
import { formatArea } from "components/identifyEventsTool/utils/formatArea";
import { useMemo } from "react";

export const EventGuide = () => {
  const { t } = useTenantTranslation();
  const { getDisplayKey } = useHotkeyContext();
  const { alt } = getModifierKey();
  const areaHotkeys = useAreaHotkeys();

  // Get the first available area hotkey for example
  const exampleAreaHotkey = useMemo(() => {
    const entries = Object.entries(areaHotkeys ?? {});
    if (entries.length === 0) return null;
    const firstEntry = entries[0];
    if (!firstEntry) return null;
    const [areaName, hotkey] = firstEntry;
    return {
      areaName,
      hotkey: hotkey.toUpperCase(),
      formattedAreaName: formatArea(areaName),
    };
  }, [areaHotkeys]);

  return (
    <Stack spacing={4}>
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
          {t("eventSequenceRules")}
        </Typography>
        <Stack spacing={1} mt={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2">{t("eventsMustAlternate")}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2">
              {t("periodCanStartWithAnyEvent")}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CancelIcon color="error" fontSize="small" />
            <Typography variant="body2">
              {t("sameEventCannotFollowItself")}
            </Typography>
          </Stack>
        </Stack>

        <Box sx={{ mt: 2, p: 1, bgcolor: "background.paper", borderRadius: 1 }}>
          <Typography variant="caption" display="block" gutterBottom>
            {t("validSequences")}
          </Typography>
          <Typography variant="body2" fontFamily="monospace">
            {"[LIFT] → [DROP] → [LIFT] → [DROP] ✓"}
          </Typography>
          <Typography variant="body2" fontFamily="monospace">
            {"[DROP] → [LIFT] → [DROP] ✓"}
          </Typography>
          <Typography variant="body2" fontFamily="monospace" color="error.main">
            {"[DROP] → [DROP] ✗"}
          </Typography>
          <Typography variant="body2" fontFamily="monospace" color="error.main">
            {"[LIFT] → [LIFT] ✗"}
          </Typography>
        </Box>
      </Alert>
      <Stack spacing={3}>
        <Typography variant="h6">
          {t("whatIsEvent", { eventType: "LIFT" })}
        </Typography>

        <Box>
          <Typography variant="body1" gutterBottom>
            {t("liftDefinition")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("liftTimingGuidance")}
          </Typography>
        </Box>

        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t("howToRecordLift")}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={2}>
            <EditableHotkey hotkeyId="liftEvent" />
            <Stack spacing={0.5}>
              <Typography variant="body2">
                1. {t("liftStep1", { hotkey: getDisplayKey("liftEvent") })}
              </Typography>
              <Typography variant="body2">2. {t("liftStep2")}</Typography>
              <Typography variant="body2">
                3. {t("liftStep3", { hotkey: getDisplayKey("liftEvent") })}
              </Typography>
              <Typography variant="body2">4. {t("liftStep4")}</Typography>
            </Stack>
          </Stack>

          <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              {t("quickAreaShortcut")}
            </Typography>
            <Typography variant="body2">
              {exampleAreaHotkey
                ? t("quickAreaShortcutDescriptionWithExample", {
                    alt,
                    hotkey: exampleAreaHotkey.hotkey,
                    areaName: exampleAreaHotkey.formattedAreaName,
                  })
                : t("quickAreaShortcutDescription", {
                    alt,
                  })}
            </Typography>
          </Alert>
        </Stack>

        <EventExampleVideoPlayer
          eventType="lift"
          buttonLabel={t("watchEventExample", { eventType: "LIFT" })}
        />
      </Stack>

      <Divider />

      <Stack spacing={3}>
        <Typography variant="h6">
          {t("whatIsEvent", { eventType: "DROP" })}
        </Typography>

        <Box>
          <Typography variant="body1" gutterBottom>
            {t("dropDefinition")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("dropTimingGuidance")}
          </Typography>
        </Box>

        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t("howToRecordDrop")}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={2}>
            <EditableHotkey hotkeyId="dropEvent" />
            <Stack spacing={0.5}>
              <Typography variant="body2">
                1. {t("dropStep1", { hotkey: getDisplayKey("dropEvent") })}
              </Typography>
              <Typography variant="body2">2. {t("dropStep2")}</Typography>
              <Typography variant="body2">
                3. {t("dropStep3", { hotkey: getDisplayKey("dropEvent") })}
              </Typography>
              <Typography variant="body2">4. {t("dropStep4")}</Typography>
            </Stack>
          </Stack>

          <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              {t("quickAreaShortcut")}
            </Typography>
            <Typography variant="body2">
              {exampleAreaHotkey
                ? t("quickAreaShortcutDescriptionWithExample", {
                    alt,
                    hotkey: exampleAreaHotkey.hotkey,
                    areaName: exampleAreaHotkey.formattedAreaName,
                  })
                : t("quickAreaShortcutDescription", {
                    alt,
                  })}
            </Typography>
          </Alert>
        </Stack>
      </Stack>
    </Stack>
  );
};
